import { mapState } from 'vuex';
import { isNumericValue } from '@/utils/vuex';
import LineChartMixin from '@/components/core/dtwin/lineChartMixin';
import { getTimeArrayFromRangeKey } from '@/components/core/dateTime/pickerParseRangeValues.js';
import { set, get } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
const boolVisual = {
  firstStateTitle: '',
  secondStateTitle: '',
  firstFaIcon: '',
  secondFaIcon: '',
  firstColor: '',
  secondColor: ''
};
export default {
  name: 'CoreDtwinStateEditVisual',
  mixins: [LineChartMixin],
  props: {
    // the current edited binding
    value: {
      type: Object,
      required: true
    },
    hideSelection: {
      type: Boolean,
      default: false
    },
    hidePreview: {
      type: Boolean,
      default: false
    },
    helpText: {
      type: String,
      required: false,
      default: () => null
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    },
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    }
  },
  data() {
    return {
      lclModel: {
        properties: {
          visual: {
            options: {}
          }
        }
      },
      lastEmit: {
        properties: {
          visual: {
            options: {}
          }
        }
      },
      updateVisual: 0,
      valueWasChanged: false,
      stateIsNull: get(this.lclModel, 'value.value') === null,
      skipTransformer: true,
      requiredRule: [v => v !== '' || this.$i18n_t('Common.fieldRequired')],
      decimalRules: [v => !!v || this.$i18n_t('Common.fieldRequired'), v => v && v < 10 || this.$i18n_t('DigitalTwin.DigitalTwin.visualization.maxDecimalPoints')],
      yAxisRules: [v => !v || !isNaN(parseFloat(v))],
      tresholdRule: true,
      rawData: false
    };
  },
  computed: {
    formIsValid() {
      return this.tresholdRule === true;
    },
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    stateValue() {
      if (this.lclModel.properties.valueType === 'json') {
        try {
          return JSON.stringify(get(this.lclModel, 'value.value'), null, 1);
        } catch (error) {
          // on error we return outside of try/catch
        }
      }
      this.sanitiseVisualOptions();
      return get(this.lclModel, 'value.value');
    },
    isNumericValue() {
      return isNumericValue(get(this.lclModel, 'value.value'));
    },
    getVisualType() {
      return get(this.lclModel, 'properties.visual.type');
    },
    isNumberType() {
      return this.lclModel.properties.valueType === 'number';
    },
    isBooleanType() {
      return this.lclModel.properties.valueType === 'boolean';
    },
    isStringType() {
      return this.lclModel.properties.valueType === 'string';
    },
    isJsonType() {
      return this.lclModel.properties.valueType === 'json';
    },
    showVisualization() {
      return this.isNumberType || this.isBooleanType || this.isStringType || this.isJsonType;
    },
    getDropdownItems() {
      if (this.isNumberType) {
        return this.numDropdownValues;
      }
      if (this.isBooleanType) {
        return this.boolDropdownValues;
      }
      if (this.isStringType || this.isJsonType) {
        return this.stringJsonDropdownValues;
      }
      return false;
    },
    numDropdownValues() {
      return [{
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.none'),
        value: null
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.gauge'),
        value: 'gauge'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.doughnut'),
        value: 'doughnut'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.odometer'),
        value: 'odometer'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.battery'),
        value: 'battery'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.lineChart'),
        value: 'lineChart'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.hidden'),
        value: 'hidden'
      }];
    },
    boolDropdownValues() {
      let booleanItems = [{
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.none'),
        value: null
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.lineChart'),
        value: 'lineChart'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.hidden'),
        value: 'hidden'
      }];
      if (this.lclModel.properties.binding.type === 'none' && this.lclModel.properties.valueType === 'boolean') {
        let buttonItem = {
          text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.button'),
          value: 'button'
        };
        booleanItems.splice(1, 0, buttonItem);
      }
      return booleanItems;
    },
    stringJsonDropdownValues() {
      return [{
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.none'),
        value: null
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.components.hidden'),
        value: 'hidden'
      }];
    },
    isLclModelValueNull() {
      return get(this.lclModel, 'value.value') === null;
    },
    boolVisualFaIcon() {
      return get(this.lclModel, 'value.value') ? this.lclModel.properties.visual.options.boolVisual.firstFaIcon : this.lclModel.properties.visual.options.boolVisual.secondFaIcon;
    },
    boolVisualFaIconColor() {
      return get(this.lclModel, 'value.value') ? this.lclModel.properties.visual.options.boolVisual.firstColor : this.lclModel.properties.visual.options.boolVisual.secondColor;
    },
    boolVisualStateTitle() {
      return get(this.lclModel, 'value.value') ? this.lclModel.properties.visual.options.boolVisual.firstStateTitle : this.lclModel.properties.visual.options.boolVisual.secondStateTitle;
    }
  },
  watch: {
    stateIsNull(val, oldVal) {
      if (val === true && oldVal !== true) {
        set(this.lclModel, 'value.value', null);
      }
    },
    stateValue() {
      this.stateIsNull = get(this.lclModel, 'value.value') === null;
    },
    'lclModel.properties.visual.options.decimalPoints': {
      handler(newVal) {
        if (parseInt(newVal) > 10) {
          this.lclModel.properties.visual.options.decimalPoints = 10;
        }
      }
    },
    minYAxis() {
      this.$emit('input');
    },
    maxYAxis() {
      this.$emit('input');
    },
    rawData: {
      handler() {
        if (this.rawData) {
          this.min = false;
          this.max = false;
          this.average = false;
          this.latest = false;
        }
        // if disabled again, enable at least 1 option by default again
        if (!this.rawData) {
          const options = ['min', 'max', 'average', 'latest'];
          // check if OTHER options are selected as well
          if (options.map(o => this[o]).filter(o => o).length === 0) {
            this.average = true;
          }
        }
        const lclModelClone = cloneDeep(this.lclModel);
        lclModelClone.properties.visual.options.rawData = this.rawData;
        this.lclModel = lclModelClone;
        this.$emit('input');
      }
    },
    value: {
      handler(newVal) {
        const lclModel = cloneDeep(newVal);
        if (!lclModel.value) {
          lclModel.value = {};
        }
        this.sanitiseVisualOptions(lclModel);
        this.setConfigStateData(lclModel);
        this.loadChartDataDebounced(lclModel);
        this.validateThresholds(lclModel);
        this.lclModel = lclModel;
        this.updateIntervalSelect(lclModel);
      },
      deep: true,
      immediate: true
    },
    lclModel: {
      handler(newVal) {
        if (isEqual(this.lastEmit, newVal)) {
          return;
        }
        this.rawData = this.lclModel.properties.visual.options.rawData === true;
        this.$emit('value:updated', newVal);
        this.lastEmit = cloneDeep(newVal);
      },
      deep: true
    }
  },
  methods: {
    shouldOptionCheckboxBeDisabled(option) {
      if (option !== 'rawData' && this.rawData) {
        return true;
      }
      if (option === 'rawData') {
        // always allow the "rawData" checkbox, see handler that will disable all other options on selection
        return false;
      }
      const options = ['min', 'max', 'average', 'latest'].filter(o => o !== option);
      // check if OTHER options are selected as well
      return options.map(o => this[o]).filter(o => o).length === 0;
    },
    sanitiseVisualOptions(lclModelClone) {
      let overwriteLclModel = false;
      if (!lclModelClone) {
        lclModelClone = cloneDeep(this.lclModel);
        overwriteLclModel = true;
      }
      if (!lclModelClone.properties.visual) {
        lclModelClone.properties.visual = {
          type: null,
          options: {}
        };
      }
      if (!lclModelClone.properties.visual.options) {
        lclModelClone.properties.visual.options = {};
      }
      if (lclModelClone.properties.valueType === 'boolean') {
        if (!get(lclModelClone, 'properties.visual.options.boolVisual')) {
          // need to deep-clone it here, otherwise the global constant retains the changes even if `editVisual` instance gets destroyed
          Object.assign(lclModelClone.properties.visual.options, {
            boolVisual: cloneDeep(boolVisual)
          });
        }
        if (lclModelClone.properties.visual.options.boolVisual.firstStateTitle === '') {
          lclModelClone.properties.visual.options.boolVisual.firstStateTitle = 'true';
        }
        if (lclModelClone.properties.visual.options.boolVisual.secondStateTitle === '') {
          lclModelClone.properties.visual.options.boolVisual.secondStateTitle = 'false';
        }
      }
      if (overwriteLclModel) {
        this.lclModel = lclModelClone;
      }
    },
    setTimeSpan() {
      const unixTs = getTimeArrayFromRangeKey(this.queryTimeSpan);
      this.queryStartDate = new Date(unixTs[0] * 1000);
      this.queryEndDate = new Date(unixTs[1] * 1000);
      const lclModelClone = cloneDeep(this.lclModel);
      lclModelClone.properties.visual.options.queryTimeSpan = this.queryTimeSpan;
      this.lclModel = lclModelClone;
      this.updateIntervalSelect();
    },
    updateIntervalSelect() {
      const [startDate, endDate] = getTimeArrayFromRangeKey(this.queryTimeSpan);
      const diff = Math.floor(endDate - startDate);
      const diffInMins = Math.floor(diff / 60);
      const diffInDays = Math.floor(diff / 60 / 60 / 24);
      this.items.forEach(item => {
        item.disabled = false;
      });
      if (diffInDays > 30) {
        // disable first three options
        this.items[0].disabled = true;
        this.items[1].disabled = true;
        this.items[2].disabled = true;
        this.interval = '1d';
      } else if (diffInDays < 30) {
        const durationsInMins = [5, 15, 60, 1440, 10080, 43200];
        let upperBoundDurationIndex = durationsInMins.findIndex(x => diffInMins < x);

        // if smaller or equal to 5 then [5], otherwise the upperBoundDurationIndex.
        // example1: upperBoundDuration is 60 -> index: 2, then allowed is index 2
        let maximumAllowedDurationIndex = upperBoundDurationIndex ? upperBoundDurationIndex : 1;
        // follow example 1: allowedDurations = ['5m', '15m']
        this.items.forEach((item, itemIndex) => {
          if (itemIndex >= maximumAllowedDurationIndex) {
            item.disabled = true;
          }
        });

        // use the minimum between max allowed and stored interval
        // if max allowed is 1h and stored interval is 15m, then use 15m
        // if max allowed is 1h and stored interval is 1d, then use 1h
        const currentIntervalIndex = this.items.findIndex(x => x.timeVal === this.interval);
        this.interval = this.items[Math.min(currentIntervalIndex, maximumAllowedDurationIndex - 1)].timeVal;
      }
    },
    getVisualCols() {
      if (!this.lclModel.properties.visual.type || this.hidePreview) {
        return 12;
      } else if (this.lclModel.properties.visual.type === 'lineChart') {
        return 6;
      }
      return 9;
    },
    sanitizeThresholds(string) {
      return string.split(',').map(item => parseFloat(item)).filter(item => !isNaN(item)).sort((a, b) => a - b);
    },
    onVisualChanged(type) {
      const lclModelClone = cloneDeep(this.lclModel);
      lclModelClone.properties.visual.type = type;
      lclModelClone.properties.visual.options = {};
      if (type === 'odometer') {
        lclModelClone.properties.visual.options.minimumLength = '8';
        lclModelClone.properties.visual.options.decimalPoints = '2';
      }
      if (type === 'gauge') {
        lclModelClone.properties.visual.options.min = '0';
        lclModelClone.properties.visual.options.max = '100';
        lclModelClone.properties.visual.options.decimalPoints = 2;
        lclModelClone.properties.visual.options.thresholds = '50';
        lclModelClone.properties.visual.options.reverse = false;
        lclModelClone.properties.visual.options.thresColors = ['#ff1744', '#fb8c00'];
      }
      this.lclModel = lclModelClone;
    },
    valueFieldEdited() {
      const lclModelClone = cloneDeep(this.lclModel);
      this.updateVisual = Date.now();
      this.valueWasChanged = true;
      const visualType = ((lclModelClone.properties || {}).visual || {}).type || null;
      if (visualType === 'gauge') {
        const threshColors = lclModelClone.properties.visual.options.thresColors;
        // filter non number items out of the comma separated string and sort array
        const thresholds = this.sanitizeThresholds(lclModelClone.properties.visual.options.thresholds);

        // write sanitized threshold values as comma separated string back to model
        lclModelClone.properties.visual.options.thresholds = thresholds.join(',');
        const diff = thresholds.length - threshColors.length;
        if (thresholds.length === 0) {
          threshColors.splice(0);
        } else if (thresholds.length < threshColors.length) {
          threshColors.splice(thresholds.length + 1, diff * -1);
        } else {
          for (let i = 0; i <= diff; i++) {
            // first color is red following colors orange
            threshColors.push(threshColors.length + i === 0 ? '#ff1744' : '#fb8c00');
          }
        }
      }
      this.lclModel = lclModelClone;
    },
    saveState() {
      if (this.stateIsNull) {
        this.lclModel.value = {
          value: null
        };
      }
      if (this.lclModel.properties.valueType === 'number') {
        set(this.lclModel, 'value.value', parseFloat(get(this.lclModel, 'value.value')));
      }
      this.lclModel.value.hasChanged = this.valueWasChanged;
      this.lclModel.value.skipTransformer = this.skipTransformer;
    },
    getDecimalPoints(decimalPoints) {
      if (decimalPoints && !isNaN(decimalPoints)) {
        return parseFloat(decimalPoints);
      } else {
        return 0;
      }
    },
    validateThresholds(lclModelClone) {
      if (!lclModelClone) {
        lclModelClone = cloneDeep(this.lclModel);
      }
      if (!get(lclModelClone, 'properties.visual.options.thresholds')) {
        return;
      }
      lclModelClone.properties.visual.options.thresholds.split(',').map(threshold => parseFloat(threshold)).find(threshold => isNaN(threshold) || threshold <= parseFloat(lclModelClone.properties.visual.options.min) || threshold >= parseFloat(lclModelClone.properties.visual.options.max)) ? this.tresholdRule = this.$i18n_t('DigitalTwin.DigitalTwin.visualization.invalidThreshold') : this.tresholdRule = true;
      this.$emit('input');
    }
  }
};